/**
 * useLicense Hook
 * Checks the license from the backend (per user, not shared localStorage).
 * isBlocked = pas de licence ET pas superadmin
 */

import { useState, useEffect } from 'react';
import { useIsSuperAdmin } from './useIsSuperAdmin';
import { userSettings as userSettingsApi } from '../services/api/client';
import { auth } from '../services/api/client';

// Module-level cache to avoid repeated API calls
// Key = userId, value = { hasLicense, timestamp }
const _licenseCache = new Map();
const _CACHE_TTL = 60000; // 1 minute

// Ongoing promise (deduplication of concurrent calls)
const _pendingFetch = new Map();

async function fetchLicenseForUser(userId) {
  const cached = _licenseCache.get(userId);
  if (cached && Date.now() - cached.timestamp < _CACHE_TTL) {
    return cached.hasLicense;
  }

  // Dedup: if a fetch is already in progress for this user, await the same one
  if (_pendingFetch.has(userId)) {
    return _pendingFetch.get(userId);
  }

  const promise = userSettingsApi.get()
    .then(settings => {
      const hasLicense = !!settings?.license_settings?.license_id;
      _licenseCache.set(userId, { hasLicense, timestamp: Date.now() });
      _pendingFetch.delete(userId);
      return hasLicense;
    })
    .catch(() => {
      _pendingFetch.delete(userId);
      // Fallback to localStorage if the API fails
      try {
        const saved = localStorage.getItem('sylink_license');
        return saved ? !!JSON.parse(saved)?.license_id : false;
      } catch {
        return false;
      }
    });

  _pendingFetch.set(userId, promise);
  return promise;
}

// Invalidate cache when license is activated (called by LicenseSettings)
export function invalidateLicenseCache(userId) {
  if (userId) _licenseCache.delete(userId);
  else _licenseCache.clear();
}

export function useLicense() {
  const [hasLicense, setHasLicense] = useState(false);
  const [licenseLoading, setLicenseLoading] = useState(true);
  const { isSuperAdmin, loading: adminLoading, user } = useIsSuperAdmin();

  useEffect(() => {
    if (adminLoading) return; // Attendre que useIsSuperAdmin ait le user

    const check = async () => {
      if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
        setHasLicense(false);
        setLicenseLoading(false);
        return;
      }
      try {
        const userId = user?.id || 'anonymous';
        const result = await fetchLicenseForUser(userId);
        setHasLicense(result);
      } catch {
        setHasLicense(false);
      } finally {
        setLicenseLoading(false);
      }
    };

    check();
  }, [adminLoading, user]);

  return {
    hasLicense,
    isSuperAdmin,
    isBlocked: !hasLicense && !isSuperAdmin,
    loading: adminLoading || licenseLoading,
  };
}

export default useLicense;
